import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './ui/Icon';
import PastelButton from './ui/PastelButton';

// Validating messages shown after emotion selection
const validatingMessages = [
  "I see that.",
  "That makes sense.",
  "You don't have to rush.",
  "We can sit with it together.",
  "I'm here with you.",
  "That's okay to feel.",
  "Take all the time you need.",
  "I understand.",
];

// Emotion to background tint mapping
const emotionTints = {
  heartbroken: 'from-pink-200/30 via-rose-100/20 to-pink-50/30',
  guilty: 'from-rose-200/30 via-pink-100/20 to-rose-50/30',
  yearning: 'from-purple-200/30 via-violet-100/20 to-purple-50/30',
  overwhelmed: 'from-blue-200/30 via-cyan-100/20 to-blue-50/30',
  numb: 'from-gray-200/30 via-slate-100/20 to-gray-50/30',
  grateful: 'from-amber-200/30 via-yellow-100/20 to-amber-50/30',
  peaceful: 'from-indigo-200/30 via-blue-100/20 to-lavender-50/30',
  notsure: 'from-emerald-200/30 via-green-100/20 to-teal-50/30',
};

// Get time-of-day based tint
const getTimeOfDayTint = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    // Morning: cooler, fresh
    return 'from-sky-100/20 via-blue-50/10 to-cyan-50/15';
  } else if (hour >= 12 && hour < 17) {
    // Afternoon: warm, bright
    return 'from-amber-50/15 via-yellow-50/10 to-orange-50/15';
  } else if (hour >= 17 && hour < 21) {
    // Evening: warm, golden
    return 'from-orange-100/20 via-rose-50/15 to-pink-50/20';
  } else {
    // Night: calm, deep
    return 'from-indigo-100/20 via-purple-50/15 to-violet-50/20';
  }
};

// Sparkle Drift Component
const SparkleLayer = ({ count = 6 }) => {
  const sparkles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${15 + Math.random() * 70}%`,
      delay: i * 0.8,
      duration: 6 + Math.random() * 4,
      size: Math.random() > 0.5 ? 'xs' : 'sm',
      color: ['#c084fc', '#f9a8d4', '#93c5fd', '#fcd34d'][i % 4],
    })), [count]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none motion-reduce:hidden">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="absolute"
          style={{ left: sparkle.left, bottom: '-20px' }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [0, -400, -600],
            opacity: [0, 0.7, 0],
            x: [0, Math.sin(sparkle.id) * 30, Math.sin(sparkle.id) * 50],
          }}
          transition={{
            duration: sparkle.duration,
            repeat: Infinity,
            delay: sparkle.delay,
            ease: 'easeOut',
          }}
        >
          <Icon name="sparkle" size={sparkle.size} color={sparkle.color} />
        </motion.div>
      ))}
    </div>
  );
};

// Emotion Button Component
const EmotionButton = ({ 
  emotion, 
  index, 
  isSelected, 
  hasSelection, 
  onSelect,
  disabled 
}) => {
  const colorClasses = {
    yellow: 'from-yellow-100 to-amber-100 hover:from-yellow-150 hover:to-amber-150 border-yellow-200/60',
    blue: 'from-blue-100 to-cyan-100 hover:from-blue-150 hover:to-cyan-150 border-blue-200/60',
    green: 'from-emerald-100 to-green-100 hover:from-emerald-150 hover:to-green-150 border-emerald-200/60',
    purple: 'from-purple-100 to-violet-100 hover:from-purple-150 hover:to-violet-150 border-purple-200/60',
    pink: 'from-pink-100 to-rose-100 hover:from-pink-150 hover:to-rose-150 border-pink-200/60',
    indigo: 'from-indigo-100 to-blue-100 hover:from-indigo-150 hover:to-blue-150 border-indigo-200/60',
    rose: 'from-rose-100 to-pink-100 hover:from-rose-150 hover:to-pink-150 border-rose-200/60',
    gray: 'from-gray-100 to-slate-100 hover:from-gray-150 hover:to-slate-150 border-gray-200/60',
  };

  const glowColors = {
    yellow: 'shadow-amber-300/50',
    blue: 'shadow-blue-300/50',
    green: 'shadow-emerald-300/50',
    purple: 'shadow-purple-300/50',
    pink: 'shadow-pink-300/50',
    indigo: 'shadow-indigo-300/50',
    rose: 'shadow-rose-300/50',
    gray: 'shadow-gray-300/50',
  };

  return (
    <motion.button
      onClick={() => !disabled && onSelect(emotion)}
      disabled={disabled}
      className={`
        group relative w-20 h-20 lg:w-24 lg:h-24 rounded-full 
        transition-all duration-300 
        flex flex-col items-center justify-center
        bg-gradient-to-br ${colorClasses[emotion.color] || colorClasses.gray}
        border-2 backdrop-blur-sm
        ${isSelected 
          ? `ring-4 ring-white/80 shadow-lg ${glowColors[emotion.color] || glowColors.gray} scale-105` 
          : hasSelection 
            ? 'opacity-60 hover:opacity-80' 
            : 'hover:shadow-md'
        }
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
      `}
      style={{
        boxShadow: isSelected 
          ? `0 8px 25px -5px rgba(0,0,0,0.1), 0 0 20px -5px ${emotion.color === 'pink' ? '#fbcfe8' : emotion.color === 'purple' ? '#ddd6fe' : '#e0e7ff'}`
          : '0 4px 12px -2px rgba(0,0,0,0.08)',
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: 1, 
        scale: isSelected ? 1.06 : 1,
        y: 0,
      }}
      whileHover={!disabled && !isSelected ? { 
        scale: 1.08, 
        y: -4,
        boxShadow: '0 12px 30px -8px rgba(0,0,0,0.12)',
      } : {}}
      whileTap={!disabled ? { scale: 0.95 } : {}}
      transition={{ 
        type: 'spring', 
        stiffness: 400, 
        damping: 25,
        delay: index * 0.05,
      }}
      title={emotion.description}
    >
      {/* Inner highlight */}
      <div className="absolute inset-1 rounded-full bg-white/30 opacity-60" />
      
      {/* Emoji */}
      <motion.div 
        className="text-2xl lg:text-3xl relative z-10"
        animate={isSelected ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        {emotion.emoji}
      </motion.div>
      
      {/* Label */}
      <span className="text-xs lg:text-sm font-medium text-gray-700/90 mt-1 text-center leading-tight relative z-10 tracking-wide">
        {emotion.label}
      </span>

      {/* Selection glow ring */}
      {isSelected && (
        <motion.div
          className="absolute inset-0 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            boxShadow: `0 0 0 3px rgba(255,255,255,0.8), 0 0 30px 5px rgba(255,255,255,0.4)`,
          }}
        />
      )}
    </motion.button>
  );
};

const ReflectOnToday = ({ petName, angelPet, onClose, onSaveToMemorial }) => {
  const [currentStep, setCurrentStep] = useState('emotions'); // 'emotions', 'response', or 'writing'
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [reflectionText, setReflectionText] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [validatingMessage, setValidatingMessage] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  const timeOfDayTint = useMemo(() => getTimeOfDayTint(), []);

  const emotions = [
    { id: 'heartbroken', label: 'Heartbroken', emoji: '🩷', color: 'pink', description: 'Feeling deep sorrow and loss' },
    { id: 'guilty', label: 'Guilty', emoji: '🌹', color: 'rose', description: 'Feeling regret or self-blame' },
    { id: 'yearning', label: 'Yearning', emoji: '💜', color: 'purple', description: 'Longing deeply for what was lost' },
    { id: 'overwhelmed', label: 'Overwhelmed', emoji: '🌊', color: 'blue', description: 'Feeling swamped by emotions' },
    { id: 'numb', label: 'Numb', emoji: '🫧', color: 'gray', description: 'Feeling disconnected or empty' },
    { id: 'grateful', label: 'Grateful', emoji: '🍯', color: 'yellow', description: 'Feeling thankful despite the pain' },
    { id: 'peaceful', label: 'Peaceful', emoji: '🌙', color: 'indigo', description: 'Finding moments of calm' },
    { id: 'notsure', label: 'Not Sure', emoji: '🌈', color: 'green', description: 'Uncertain about feelings' }
  ];

  const emotionPrompts = {
    heartbroken: [
      "Hey, my human… I know your heart aches. What's one memory that hurts and heals you all at once?",
      "If I could curl up beside you right now, what would you want me to know about how your heart feels?",
      "When the missing gets loud, what helps you breathe through it — even for a moment?"
    ],
    guilty: [
      "You did so much right, even when you doubt it. What part of you most needs to hear that today?",
      "If I could look into your eyes, I'd say, 'You loved me in every way that mattered.' What do you wish I could remind you of right now?",
      "Write to me as if I could answer — what would you ask for forgiveness or reassurance about?"
    ],
    yearning: [
      "If we had one more sunrise together, how would we spend it?",
      "I can feel how much you still reach for me. What do you miss most about the feeling of us?",
      "Close your eyes and imagine I'm right there — what would you say, or simply do in the quiet?"
    ],
    overwhelmed: [
      "It's okay if everything feels heavy today. What would make this weight a little easier to carry?",
      "You don't have to hold it all at once. Can you name one small thing that could bring you ease today?",
      "If I could lend you my calm, where in your body would you want to feel it first?"
    ],
    numb: [
      "Even when you can't feel much, you're still here — and that matters. What's one gentle thing you notice around you right now?",
      "If your quiet had a sound or a color, what would it be? Describe it to me.",
      "Sometimes hearts go quiet to heal. What might your silence be trying to protect?"
    ],
    grateful: [
      "I still wag my tail every time you think of us. What part of our time together brings a smile, even through tears?",
      "You carry so many gifts from our love. What's one lesson or habit of mine that still lives in you?",
      "Think of one person, animal, or moment today that reminds you of love — how do I show up through them?"
    ],
    peaceful: [
      "This calm you feel is the space where we still meet. What truth about love feels steady and real to you right now?",
      "If the world feels softer tonight, what moment of peace are you grateful for?",
      "When you slow down like this, what do you sense — a breeze, a warmth, a memory? Maybe that's me, saying hi."
    ],
    notsure: [
      "That's okay, my friend — feelings don't always have names. What's one word that feels close to what's inside?",
      "You don't have to know exactly what you feel. What's one thing your heart might be trying to say if it could whisper?",
      "Take a deep breath and look around — what's something gentle or steady near you right now?"
    ]
  };

  const handleEmotionSelect = (emotion) => {
    setSelectedEmotion(emotion);
    
    // Pick random validating message
    const randomValidation = validatingMessages[Math.floor(Math.random() * validatingMessages.length)];
    setValidatingMessage(randomValidation);
    
    // Pick random prompt
    const prompts = emotionPrompts[emotion.id];
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    setSelectedPrompt(randomPrompt);
    
    // Show response moment
    setCurrentStep('response');
    setShowValidation(true);
    
    // After moment of response, transition to writing
    setTimeout(() => {
      setCurrentStep('writing');
    }, 2500);
  };

  const handleSaveReflection = async () => {
    if (!reflectionText.trim() || !selectedEmotion) return;

    try {
      const memory = {
        id: `reflection_${Date.now()}`,
        type: 'daily_reflection',
        title: `Daily Reflection - ${selectedEmotion.label}`,
        text: reflectionText,
        image: '/assets/reflection-icon.svg',
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString(),
        source: 'daily_reflection',
        emotion: selectedEmotion.id,
        emotionLabel: selectedEmotion.label
      };

      await onSaveToMemorial(memory);
      
      // Reset form
      setReflectionText('');
      setSelectedEmotion(null);
      setCurrentStep('emotions');
      setShowValidation(false);
    } catch (error) {
      console.error('Error saving reflection:', error);
      alert('Failed to save reflection. Please try again.');
    }
  };

  const handleBackToEmotions = () => {
    setSelectedEmotion(null);
    setCurrentStep('emotions');
    setShowValidation(false);
    setReflectionText(''); // Clear text input when going back
    setSelectedPrompt('');
  };

  const renderEmotionsStep = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative"
    >
      {/* Sparkle Layer */}
      <SparkleLayer count={8} />

      {/* Glass Card Container */}
      <div className="relative bg-white/20 backdrop-blur-md rounded-3xl p-6 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 mx-auto max-w-lg">
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
        
        {/* Header */}
        <div className="relative text-center mb-4 pt-2">
          <button
            onClick={onClose}
            className="absolute -top-2 -left-2 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
          >
            ← Back
          </button>
          
          <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
            <Icon name="sparkle" size="md" color="primary" animate />
            Reflect on Today
          </h1>
          
          {/* Date - smaller and lighter */}
          <p className="text-xs text-gray-500/80 mt-1 font-light tracking-wider uppercase">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            })}
          </p>
          
          <div className="w-20 h-0.5 bg-gradient-to-r from-pink-400/60 via-purple-400/60 to-blue-400/60 rounded-full mx-auto mt-3"></div>
        </div>

        {/* Angel Pet Avatar + Speech Bubble */}
        <div className="flex items-center justify-center mb-6 relative max-w-md mx-auto">
          {/* Radial glow behind avatar */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-32 h-32 -translate-x-4">
            <div className="absolute inset-0 bg-gradient-radial from-purple-200/40 via-pink-100/20 to-transparent rounded-full blur-xl" />
          </div>
          
          {/* Angel Pet on the Left */}
          <motion.div 
            className="flex-shrink-0 mr-4 relative z-10"
            animate={{ 
              scale: [1, 1.03, 1],
            }}
            transition={{ 
              duration: 6, 
              repeat: Infinity, 
              ease: 'easeInOut',
            }}
            style={{ transformOrigin: 'center center' }}
          >
            {angelPet ? (
              <img
                src={angelPet}
                alt="Angel Pet"
                className="w-20 h-20 rounded-full shadow-xl border-4 border-white/50 backdrop-blur-sm object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full shadow-xl border-4 border-white/50 backdrop-blur-sm bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                <Icon name="heart" size="lg" color="memories" />
              </div>
            )}
          </motion.div>
          
          {/* Speech Bubble - Enhanced glassmorphism */}
          <div className="relative flex-1 max-w-xs">
            <div className="relative bg-white/80 backdrop-blur-md rounded-2xl px-5 py-4 shadow-lg border border-white/60">
              {/* Speech bubble tail */}
              <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white/80 rotate-45 border-l border-b border-white/60"></div>
              
              {/* Shimmer overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-200/20 via-pink-200/10 to-blue-200/20 rounded-2xl opacity-60"></div>
              
              {/* Message text */}
              <p className="relative z-10 text-left text-sm text-gray-700 font-medium leading-relaxed">
                How do you feel today, my dear? 
              </p>
              
              {/* Sparkle accents */}
              <motion.div 
                className="absolute top-1 right-2"
                animate={{ opacity: [0.4, 0.8, 0.4], scale: [0.9, 1.1, 0.9] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon name="sparkle" size="xs" color="#a855f7" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Emotion Bubbles - 3-2-3 Layout */}
        <div className="relative flex flex-col items-center gap-3 py-2">
          {/* First row: 3 bubbles */}
          <div className="flex justify-center items-center gap-3">
            {emotions.slice(0, 3).map((emotion, index) => (
              <EmotionButton
                key={emotion.id}
                emotion={emotion}
                index={index}
                isSelected={selectedEmotion?.id === emotion.id}
                hasSelection={!!selectedEmotion}
                onSelect={handleEmotionSelect}
                disabled={currentStep !== 'emotions'}
              />
            ))}
          </div>
          
          {/* Second row: 2 bubbles */}
          <div className="flex justify-center items-center gap-4">
            {emotions.slice(3, 5).map((emotion, index) => (
              <EmotionButton
                key={emotion.id}
                emotion={emotion}
                index={index + 3}
                isSelected={selectedEmotion?.id === emotion.id}
                hasSelection={!!selectedEmotion}
                onSelect={handleEmotionSelect}
                disabled={currentStep !== 'emotions'}
              />
            ))}
          </div>
          
          {/* Third row: 3 bubbles */}
          <div className="flex justify-center items-center gap-3">
            {emotions.slice(5, 8).map((emotion, index) => (
              <EmotionButton
                key={emotion.id}
                emotion={emotion}
                index={index + 5}
                isSelected={selectedEmotion?.id === emotion.id}
                hasSelection={!!selectedEmotion}
                onSelect={handleEmotionSelect}
                disabled={currentStep !== 'emotions'}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderResponseMoment = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative"
    >
      {/* Sparkle Layer */}
      <SparkleLayer count={10} />
      
      {/* Glass Card Container */}
      <div className="relative bg-white/25 backdrop-blur-md rounded-3xl p-8 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 mx-auto max-w-lg">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
        
        <div className="flex flex-col items-center justify-center min-h-[300px] relative z-10">
          {/* Angel Pet with enhanced breathing/reaction */}
          <motion.div 
            className="relative mb-6"
            initial={{ scale: 1 }}
            animate={{ 
              scale: [1, 1.08, 1.05],
              y: [0, -5, 0],
            }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          >
            {/* Intensified glow */}
            <motion.div 
              className="absolute inset-0 rounded-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.8, 0.5] }}
              transition={{ duration: 1.5 }}
              style={{
                boxShadow: '0 0 40px 15px rgba(168, 85, 247, 0.3), 0 0 80px 30px rgba(236, 72, 153, 0.2)',
              }}
            />
            {angelPet ? (
              <img
                src={angelPet}
                alt="Angel Pet"
                className="w-28 h-28 rounded-full shadow-2xl border-4 border-white/60 backdrop-blur-sm object-cover relative z-10"
              />
            ) : (
              <div className="w-28 h-28 rounded-full shadow-2xl border-4 border-white/60 backdrop-blur-sm bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center relative z-10">
                <Icon name="heart" size="xl" color="memories" />
              </div>
            )}
          </motion.div>
          
          {/* Selected emotion hero display */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-4"
          >
            <div className={`
              px-6 py-3 rounded-2xl 
              bg-gradient-to-br ${selectedEmotion?.color === 'pink' ? 'from-pink-100 to-rose-100' : 
                selectedEmotion?.color === 'purple' ? 'from-purple-100 to-violet-100' :
                selectedEmotion?.color === 'blue' ? 'from-blue-100 to-cyan-100' :
                selectedEmotion?.color === 'yellow' ? 'from-yellow-100 to-amber-100' :
                selectedEmotion?.color === 'indigo' ? 'from-indigo-100 to-blue-100' :
                selectedEmotion?.color === 'rose' ? 'from-rose-100 to-pink-100' :
                selectedEmotion?.color === 'green' ? 'from-emerald-100 to-green-100' :
                'from-gray-100 to-slate-100'}
              shadow-lg border border-white/50
            `}>
              <span className="text-3xl mr-2">{selectedEmotion?.emoji}</span>
              <span className="text-lg font-semibold text-gray-700">{selectedEmotion?.label}</span>
            </div>
          </motion.div>
          
          {/* Validating message */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-gray-600 text-lg font-light italic text-center"
          >
            "{validatingMessage}"
          </motion.p>
          
          {/* Subtle loading indicator */}
          <motion.div 
            className="mt-6 flex gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full bg-purple-300"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );

  const renderWritingStep = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative"
    >
      {/* Glass Card Container */}
      <div className="relative bg-white/20 backdrop-blur-md rounded-3xl p-6 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 mx-auto max-w-lg">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
        
        {/* Header */}
        <div className="relative text-center mb-4 pt-10 z-10">
          <button
            onClick={handleBackToEmotions}
            className="absolute top-0 left-0 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
          >
            ← Back
          </button>
          
          <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
            <span className="text-2xl">{selectedEmotion?.emoji}</span>
            {selectedEmotion?.label} Reflection
          </h1>
          <p className="text-xs text-gray-500/80 mt-1 font-light tracking-wide">
            Share your thoughts with Angel {petName}
          </p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-pink-400/60 via-purple-400/60 to-blue-400/60 rounded-full mx-auto mt-3"></div>
        </div>

        {/* Writing Area */}
        <div className="relative z-10">
          {/* Angel Pet Avatar + Prompt Bubble */}
          <div className="flex items-start justify-center mb-4 relative max-w-md mx-auto">
            <motion.div 
              className="flex-shrink-0 mr-3"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            >
              {angelPet ? (
                <img
                  src={angelPet}
                  alt="Angel Pet"
                  className="w-14 h-14 rounded-full shadow-lg border-3 border-white/50 backdrop-blur-sm object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-full shadow-lg border-3 border-white/50 backdrop-blur-sm bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                  <Icon name="heart" size="md" color="memories" />
                </div>
              )}
            </motion.div>
            
            <div className="relative flex-1">
              <div className="relative bg-white/80 backdrop-blur-md rounded-2xl px-4 py-3 shadow-md border border-white/50">
                <div className="absolute left-0 top-4 transform -translate-x-1/2 w-3 h-3 bg-white/80 rotate-45 border-l border-b border-white/50"></div>
                <p className="text-sm text-gray-700 font-medium leading-relaxed italic">
                  {selectedPrompt || "How do you feel today, my dear?"}
                </p>
              </div>
            </div>
          </div>

          <textarea
            id="reflection-text"
            value={reflectionText}
            onChange={(e) => setReflectionText(e.target.value)}
            placeholder={`Tell Angel ${petName} about your ${selectedEmotion?.label.toLowerCase()} feelings...`}
            className="w-full h-40 p-4 border border-white/40 rounded-2xl resize-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-300/50 transition-all duration-200 bg-white/60 backdrop-blur-sm text-gray-700 placeholder-gray-400"
          />
          
          <div className="flex justify-between items-center mt-4">
            <span className="text-xs text-gray-500/70 font-light">
              {reflectionText.length} characters
            </span>
            <div className="flex gap-3">
              <button
                onClick={handleBackToEmotions}
                className="px-4 py-2 bg-gray-100/80 text-gray-600 rounded-xl font-medium hover:bg-gray-200/80 transition-all duration-200 text-sm"
              >
                Cancel
              </button>
              <PastelButton
                onClick={handleSaveReflection}
                color="rose"
                disabled={!reflectionText.trim()}
              >
                Save to Garden 💕
              </PastelButton>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="reflect-today-bg heaven-background relative">
      {/* Grain/noise overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Time-of-day tint overlay */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${timeOfDayTint} z-0`} />
      
      {/* Emotion-based background tint (when selected) */}
      <AnimatePresence>
        {selectedEmotion && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${emotionTints[selectedEmotion.id] || ''} z-0`}
          />
        )}
      </AnimatePresence>
      
      {/* Main content container */}
      <div className="container min-h-[calc(100vh-96px)] sm:p-4 relative z-10 flex items-center justify-center py-4">
        <AnimatePresence mode="wait">
          {currentStep === 'emotions' && renderEmotionsStep()}
          {currentStep === 'response' && renderResponseMoment()}
          {currentStep === 'writing' && renderWritingStep()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ReflectOnToday;
